import React from 'react';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { useMsal } from '@azure/msal-react';
import useProfileData from '../../services/ProfileDataService';
import isSupportedTypes from '../../Helpers/isSupportedTypes';
import { getSelectedStatusID } from '../../Utilities/getSelectedStatusID';
import { IsValidStatus } from '../../Utilities/IsValidStatus';
import { updateOrderStatus } from '../../Helpers/Hooks/UseUpdateOrderStatus';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GET_ORDER_DETAIL } from '../../Helpers/queries/GetOrderDetails';

const CustomDropdownCell = ({
	field,
	dataItem,
	updateData,
	disabled,
	onFetchData,
	OnStatusChangeError,
	employeeRole,
	employeeData,
}) => {
	const { accounts } = useMsal();
	const { token } = useProfileData(false);
	const queryClient = useQueryClient();

	function validateStatusChange(selectedStatus) {
		//Disable if order has not being assigned
		if (dataItem.designerDetail === 'Not Assigned') {
			return false;
		}

		return IsValidStatus(dataItem.itemType, selectedStatus, dataItem);
	}

	const options = {
		Special: [
			{
				id: 1,
				name: 'Not started',
				disabled: !validateStatusChange('Not started'),
			},
			{
				id: 2,
				name: 'Order Processing',
				disabled: !validateStatusChange('Order Processing'),
			},
			{
				id: 3,
				name: 'Ready for Drafting',
				disabled: !validateStatusChange('Ready for Drafting'),
			},
			{
				id: 4,
				name: 'WIP',
				disabled: !validateStatusChange('WIP'),
			},
			{
				id: 5,
				name: 'Ready for Check',
				disabled: !validateStatusChange('Ready for Check'),
			},
			{
				id: 6,
				name: 'Submitted',
				disabled: !validateStatusChange('Submitted'),
			},
			{
				id: 7,
				name: 'Released',
				disabled: !validateStatusChange('Released'),
			},
		],
		Standard: [
			{
				id: 1,
				name: 'Not started',
				disabled: !validateStatusChange('Not started'),
			},
			{
				id: 2,
				name: 'Submitted',
				disabled: !validateStatusChange('Submitted'),
			},
			{
				id: 3,
				name: 'Released',
				disabled: !validateStatusChange('Released'),
			},
		],
	};

	//Unassign detailer when change status to Ready for drafting
	//uncomment this code if at some point is needed to follow bussines logic like before on rooftop

	// const handleUnassign = async () => {

	//   let employeeTitleRelIdValue;

	//   employeeData.forEach(item => {
	//     if (item.employeeTitleId === 1) {
	//       employeeTitleRelIdValue = item.employeeTitleRelId;
	//     }
	//   });

	//   let valuesList = [];

	//   let mappedValues = {
	//     orderDetailId: dataItem.orderDetailId,
	//     employeeEmail: accounts[0].username,
	//     employeeTitleId: 1,
	//     hoursActual: 0,
	//     author: truncateEmail(accounts[0].username, 50),
	//     isActive: 0,
	//     userRole: employeeRole,
	//     employeeTitleRelId: employeeTitleRelIdValue,
	//   };

	//   valuesList = [mappedValues];

	//   try {
	//     if (token && dataItem.orderDetailId) {
	//       const response = await axios.post(
	//         `orderAssignation/`,
	//         valuesList,
	//         {
	//           headers: {
	//             Authorization: token,
	//           },
	//         }
	//       );

	//     }

	//   } catch (error) {
	//     OnStatusChangeError();
	//   };
	// };

	const mutation = useMutation({
		mutationFn: updateOrderStatus,
		onError: (error) => {
			OnStatusChangeError(error, 'error');
		},
		onSuccess: () => {
			onFetchData();
			queryClient.invalidateQueries([GET_ORDER_DETAIL]);      
		},
	});

	const handleChange = (e) => {
		const updatedData = { ...dataItem, [field]: e.target.value.name };
		const userName = accounts[0].name;

		if (dataItem.designerDetail === userName) {
			updateData(updatedData);

			const correctSelectedStatus = validateStatusChange(e.target.value.name);
			const status = getSelectedStatusID(e.target.value.name);

			if (
				token &&
				(correctSelectedStatus || isSupportedTypes(dataItem.itemType))
			) {
				mutation.mutate({
					status,
					orderDetailId: dataItem.orderDetailId,
					token,
					accounts,
				});
			}
		} else {
			OnStatusChangeError(
				'Cannot change status for orders that are not assigned to yourself.',
				'error'
			);
		}
	};

	const itemRenderFIN = (li, itemProps) => {
		const index = itemProps.index;
		let style = {
			color: '#1C2325',
			backgroundColor: '#F0F0F0',
			borderRadius: '180px',
			padding: '5px',
		};

		switch (index) {
			// Not started
			case 0:
				if (itemProps.dataItem.disabled) {
					style.backgroundColor = '#EBEBEB';
					style.color = '#8C8C8C';
				} else {
					style.backgroundColor = '#DEEFEF';
				}
				break;
			// Submitted
			case 1:
				if (itemProps.dataItem.disabled) {
					style.backgroundColor = '#EBEBEB';
					style.color = '#8C8C8C';
				} else {
					style.backgroundColor = '#9FF28F';
				}
				break;
			// Released
			case 2:
				if (itemProps.dataItem.disabled) {
					style.backgroundColor = '#EBEBEB';
					style.color = '#8C8C8C';
				} else {
					style.color = 'white';
					style.backgroundColor = '#007F0E';
				}
				break;
			default:
				style.color = '#00F';
		}

		const itemChildren = (
			<span className="roboto-15-400" style={style}>
				{li.props.children}
			</span>
		);

		return React.cloneElement(li, li.props, itemChildren);
	};

	const itemRenderSPC = (li, itemProps) => {
		const index = itemProps.index;
		let style = {
			color: '#1C2325',
			backgroundColor: '#F0F0F0',
			borderRadius: '180px',
			padding: '5px',
			whiteSpace: 'nowrap',
		};

		switch (index) {
			// Not started
			case 0:
				if (itemProps.dataItem.disabled) {
					style.backgroundColor = '#EBEBEB';
					style.color = '#8C8C8C';
				} else {
					style.backgroundColor = '#DEEFEF';
				}
				break;
			// Order P
			case 1:
				if (itemProps.dataItem.disabled) {
					style.backgroundColor = '#EBEBEB';
					style.color = '#8C8C8C';
				} else {
					style.backgroundColor = '#CDB8EB';
				}
				break;
			// Ready for drafting
			case 2:
				if (itemProps.dataItem.disabled) {
					style.backgroundColor = '#EBEBEB';
					style.color = '#8C8C8C';
				} else {
					style.backgroundColor = '#FFE7CD';
				}
				break;
			// WIP
			case 3:
				if (itemProps.dataItem.disabled) {
					style.backgroundColor = '#EBEBEB';
					style.color = '#8C8C8C';
				} else {
					style.backgroundColor = '#FFF890';
				}
				break;
			// Ready for check
			case 4:
				if (itemProps.dataItem.disabled) {
					style.backgroundColor = '#EBEBEB';
					style.color = '#8C8C8C';
				} else {
					style.color = 'white';
					style.backgroundColor = '#0097E0';
					style.border = '1px solid white';
				}
				break;
			// Submitted
			case 5:
				if (itemProps.dataItem.disabled) {
					style.backgroundColor = '#EBEBEB';
					style.color = '#8C8C8C';
				} else {
					style.backgroundColor = '#9FF28F';
				}
				break;
			// Released
			case 6:
				if (itemProps.dataItem.disabled) {
					style.backgroundColor = '#EBEBEB';
					style.color = '#8C8C8C';
				} else {
					style.color = 'white';
					style.backgroundColor = '#007F0E';
				}
				break;
			default:
				style.color = '#00F';
		}
		// }

		const itemChildren = (
			<span className="roboto-15-400" style={style}>
				{li.props.children}
			</span>
		);

		return React.cloneElement(li, li.props, itemChildren);
	};

	const valueRender = (element) => {
		let style = {
			color: '#1C2325',
			backgroundColor: '#F0F0F0',
			borderRadius: '180px',
			padding: '5px',
		};

		if (disabled) {
			style.backgroundColor = '#EBEBEB';
			style.color = '#8C8C8C';
		} else {
			switch (dataItem[field]) {
				// Not started
				case 'Not Started':
					style.backgroundColor = '#DEEFEF';
					break;
				// Order P
				case 'Order Processing':
					style.backgroundColor = '#CDB8EB';
					break;
				// Ready for drafting
				case 'Ready for Drafting':
					style.backgroundColor = '#FFE7CD';
					break;
				// WIP
				case 'WIP':
					style.backgroundColor = '#FFF890';
					break;
				// Ready for check
				case 'Ready for Check':
					style.color = 'white';
					style.backgroundColor = '#0097E0';
					break;
				// Submitted
				case 'Submitted':
					style.backgroundColor = '#9FF28F';
					break;
				// Released
				case 'Released':
					style.color = 'white';
					style.backgroundColor = '#007F0E';
					break;
				default:
					style.color = '#00F';
			}
		}

		const itemChildren = (
			<span className="roboto-15-400 element.style" style={style}>
				{/* {element.props.children} */}
				{dataItem.esoistatusDesc}
			</span>
		);
		return React.cloneElement(element, element.props, itemChildren);
	};

	const dropdownOptions = isSupportedTypes(dataItem.itemType)
		? options.Special
		: options.Standard;
	const itemRender = isSupportedTypes(dataItem.itemType)
		? itemRenderSPC
		: itemRenderFIN;
	return (
		<td>
			<DropDownList
				style={{
					backgroundColor: 'transparent',
					border: '1px solid transparent',
				}}
				defaultValue={dropdownOptions[0]}
				value={dataItem[field]}
				data={dropdownOptions}
				dataItemKey="id"
				textField="name"
				itemRender={itemRender}
				valueRender={valueRender}
				onChange={handleChange}
				disabled={disabled}
				skipDisabledItems={true}
			/>
		</td>
	);
};

export default CustomDropdownCell;
